import React, {useEffect} from 'react';
import {useOnboardingContext} from './Onboarding';
import {ScrollableFlex, WrapperFlex} from '../utils/scrolling';
import {
  Box,
  Button,
  Code,
  Divider,
  Flex,
  ListItem,
  OrderedList,
  Skeleton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import {
  useEnableIntegration,
  useIntegrations,
} from '../../hooks/api/integrations';
import {CodeSnippet} from '../CodeSnippet';
import {getEmbedScript} from '../Integrations/WebIntentIntegrationDetails';
import {useCustomer} from '../../hooks/api/metadata';
import {VerifyWebIntentInstallationModal} from '../Integrations/VerifyWebIntentInstallationModal';

export const InstallWebIntent = () => {
  const customer = useCustomer();
  const {onPageNavigate, isFirstStep} = useOnboardingContext();
  const {data: integrations, isPending: isIntegrationsPending} =
    useIntegrations({});
  const {mutate: enableIntegration, isPending: isEnableIntegrationPending} =
    useEnableIntegration();

  const webIntentIntegration = integrations?.find(
    (integration) => integration.type === 'webIntent'
  );

  const verifyModal = useDisclosure();

  // Enable the integration if it is not yet enabled
  useEffect(() => {
    if (isIntegrationsPending || isEnableIntegrationPending) {
      return;
    }

    if (!webIntentIntegration) {
      enableIntegration({type: 'webIntent'});
    }
  }, [
    integrations,
    isIntegrationsPending,
    isEnableIntegrationPending,
    webIntentIntegration,
    enableIntegration,
  ]);

  return (
    <WrapperFlex>
      <VerifyWebIntentInstallationModal
        isOpen={verifyModal.isOpen}
        onClose={verifyModal.onClose}
      />
      <ScrollableFlex gap={12}>
        <Flex direction="column" gap={5}>
          <Box fontSize="2xl">
            Also on your website: install the Keyplay tracking snippet
          </Box>
          <Box color="kgray.300">
            Required - add a lightweight snippet to your website so Keyplay can
            track visitor intent. You or a teammate can install it in under 5
            minutes.
          </Box>
        </Flex>
        <Flex direction="column" gap={5}>
          <OrderedList fontSize="lg" spacing={8} listStylePosition="inside">
            <ListItem>
              <Box as="span" display="inline">
                Copy your unique tracking snippet
              </Box>
              {webIntentIntegration ? (
                <Box mt={4}>
                  <CodeSnippet
                    code={getEmbedScript({
                      profileId: webIntentIntegration.trackingScriptId,
                      customerId: customer._id,
                    })}
                  />
                </Box>
              ) : (
                <Skeleton borderRadius="xl" height="200px" mt={4} />
              )}
            </ListItem>
            <ListItem>
              <Box as="span" display="inline">
                Paste it into the <Code fontSize="sm">&lt;head&gt;</Code>{' '}
                section of your HTML
              </Box>
            </ListItem>
            <ListItem>
              <Box as="span" display="inline">
                Make sure everything is working correctly by verifying your
                installation.
              </Box>
              <Flex align="center" gap={3} mt={4}>
                <Button
                  colorScheme="kbuttonblue"
                  isDisabled={!!webIntentIntegration?.trackingScriptDetectedAt}
                  onClick={verifyModal.onOpen}
                >
                  Verify Installation
                </Button>
                {webIntentIntegration?.trackingScriptDetectedAt && (
                  <Text fontSize="sm" color="kgreen.400">
                    Congrats! Your tracking snippet is installed and working
                    correctly.
                  </Text>
                )}
              </Flex>
            </ListItem>
          </OrderedList>
        </Flex>
      </ScrollableFlex>

      <Divider />

      <Flex justifyContent="space-between" w="100%" px={10} py={4}>
        {!isFirstStep && (
          <Button
            colorScheme="kbuttonblue"
            onClick={() => onPageNavigate('prev')}
            variant="outline"
            w="110px"
          >
            Back
          </Button>
        )}
        <Box flexGrow={1} />
        <Button
          colorScheme="kbuttonblue"
          isDisabled={!webIntentIntegration?.trackingScriptDetectedAt}
          onClick={() => onPageNavigate('next')}
          variant="solid"
        >
          Continue
        </Button>
      </Flex>
    </WrapperFlex>
  );
};
